<template>
  <b-overlay :show="loading" variant="transparent" opacity="0.99" blur="5px" rounded="sm">
    <template #overlay>
      <div class="text-center">
        <b-icon-controller font-scale="3" animation="cylon" />
        <p>กรุณารอสักครู่...</p>
      </div>
    </template>

    <div class="card mb-1">
      <div class="card-body">
        <h4 class="mb-1"><span class="text-muted">AutoPeer Mbet /</span> <strong>รายการฝาก</strong></h4>
        <div class="row">
          <div class="col-md-2">
            <label>Username</label>
            <input v-model="filters.username" type="text" class="form-control" placeholder="Username" @keyup.enter="fetchDeposits" />
          </div>
          <div class="col-md-2">
            <label>สถานะ</label>
            <b-form-select v-model="filters.status" :options="statusOptions" />
          </div>
          <div class="col-md-2">
            <label>วันที่เริ่มต้น</label>
            <flat-pickr v-model="filters.startDate" class="form-control" :config="{ enableTime: true, dateFormat: 'Y-m-d H:i', time_24hr: true }" />
          </div>
          <div class="col-md-2">
            <label>วันที่สิ้นสุด</label>
            <flat-pickr v-model="filters.endDate" class="form-control" :config="{ enableTime: true, dateFormat: 'Y-m-d H:i', time_24hr: true }" />
          </div>
          <div class="col-md-2 d-flex align-items-end">
            <button class="btn btn-gradient-dark" @click="fetchDeposits">
              <feather-icon icon="SearchIcon" /> ค้นหา
            </button>
          </div>
          <div class="col d-flex align-items-end justify-content-end">
            <button class="btn btn-outline-warning" @click="showClearBlockModal = true">
              <feather-icon icon="UnlockIcon" /> ปลด Block
            </button>
          </div>
        </div>
      </div>
    </div>

    <b-card no-body>
      <b-table small striped hover responsive show-empty :items="items" :fields="fields" :per-page="perPage" class="position-relative">
        <template #cell(index)="data">{{ perPage * (currentPage - 1) + (data.index + 1) }}</template>
        <template #cell(amount)="data">{{ formatNum(data.item.amount) }}</template>
        <template #cell(status)="data">
          <span class="badge" :class="statusClass(data.item.status)">{{ data.item.status }}</span>
        </template>
        <template #cell(createdAt)="data">{{ formatDate(data.item.createdAt) }}</template>
        <template #cell(destinationAccount)="data">
          <div v-if="getDestinationAccount(data.item)" class="text-nowrap">
            <small class="d-block font-weight-bold">{{ getDestinationAccount(data.item).bankName }}</small>
            <small class="d-block text-muted">{{ getDestinationAccount(data.item).accountNumber }}</small>
            <small class="d-block text-muted">{{ getDestinationAccount(data.item).firstName }} {{ getDestinationAccount(data.item).lastName }}</small>
          </div>
          <span v-else class="text-muted">-</span>
        </template>
        <template #cell(actions)="data">
          <b-button
            v-if="data.item.status === 'processing'"
            variant="outline-primary"
            size="sm"
            @click="openSlipModal(data.item)"
          >
            <feather-icon icon="UploadIcon" size="14" />
            แนบสลิป
          </b-button>
          <span v-else class="text-muted">-</span>
        </template>
        <template #empty>
          <div class="p-2 text-center"><img src="/box.png" alt="empty" height="40" /> ไม่พบข้อมูล</div>
        </template>
      </b-table>

      <b-card-body class="d-flex justify-content-between flex-wrap pt-0">
        <b-form-group label="Per Page" label-cols="8" label-align="left" label-size="sm" class="text-nowrap mb-md-0 mr-1">
          <b-form-select v-model="perPage" size="sm" :options="[10,20,50,100]" @change="fetchDeposits" />
        </b-form-group>
        <b-pagination v-model="currentPage" :total-rows="totalRows" :per-page="perPage" first-number last-number class="mb-0" @input="fetchDeposits">
          <template #prev-text><feather-icon icon="ChevronLeftIcon" size="18" /></template>
          <template #next-text><feather-icon icon="ChevronRightIcon" size="18" /></template>
        </b-pagination>
      </b-card-body>
    </b-card>

    <!-- Upload Slip Modal -->
    <b-modal
      v-model="showSlipModal"
      title="แนบสลิปแทน Member"
      ok-title="อัปโหลดสลิป"
      cancel-title="ยกเลิก"
      :ok-disabled="!slipFile || slipUploading"
      @ok="submitSlip"
      @hidden="resetSlipModal"
    >
      <div v-if="slipTarget" class="mb-1">
        <p class="mb-50"><strong>Username:</strong> {{ slipTarget.username }}</p>
        <p class="mb-50"><strong>จำนวนเงิน:</strong> {{ formatNum(slipTarget.amount) }} บาท</p>
        <p class="mb-0"><strong>สถานะ:</strong> <span class="badge" :class="statusClass(slipTarget.status)">{{ slipTarget.status }}</span></p>
      </div>
      <hr />
      <b-form-group label="เลือกรูปสลิป">
        <b-form-file
          v-model="slipFile"
          accept="image/*"
          placeholder="เลือกไฟล์รูปสลิป..."
          drop-placeholder="วางไฟล์ที่นี่..."
          @input="previewSlip"
        />
      </b-form-group>
      <div v-if="slipPreview" class="text-center mt-1">
        <img :src="slipPreview" alt="slip preview" style="max-width: 100%; max-height: 300px; border-radius: 8px; border: 1px solid #ddd;" />
      </div>
      <div v-if="slipUploading" class="text-center mt-1">
        <b-spinner small variant="primary" /> กำลังอัปโหลด...
      </div>
    </b-modal>

    <!-- Clear Block Modal -->
    <b-modal v-model="showClearBlockModal" title="ปลด Block Member" ok-title="ปลด Block" cancel-title="ยกเลิก" @ok="clearBlock" :ok-disabled="!clearBlockUsername || clearBlockLoading">
      <p class="text-muted mb-1">กรอก Username ของ Member ที่ต้องการปลด Block (เช่น หลังยกเลิกรายการฝาก จะถูกระงับ 3 นาที)</p>
      <b-form-group label="Username">
        <input v-model="clearBlockUsername" type="text" class="form-control" placeholder="กรอก username" @keyup.enter="clearBlock" />
      </b-form-group>
      <div v-if="clearBlockLoading" class="text-center">
        <b-spinner small variant="primary" /> กำลังดำเนินการ...
      </div>
    </b-modal>
  </b-overlay>
</template>

<script>
import moment from 'moment-timezone'
import flatPickr from 'vue-flatpickr-component'
import {
  BOverlay, BIconController, BCard, BCardBody, BTable, BFormGroup,
  BFormSelect, BPagination, BModal, BSpinner, BButton, BFormFile,
} from 'bootstrap-vue'

export default {
  components: {
    flatPickr, BOverlay, BIconController, BCard, BCardBody, BTable,
    BFormGroup, BFormSelect, BPagination, BModal, BSpinner, BButton, BFormFile,
  },
  data() {
    return {
      loading: false,
      items: [],
      totalRows: 0,
      currentPage: 1,
      perPage: 20,
      filters: {
        username: '',
        status: '',
        startDate: moment().tz('Asia/Bangkok').format('YYYY-MM-DD 00:00'),
        endDate: moment().tz('Asia/Bangkok').format('YYYY-MM-DD 23:59'),
      },
      statusOptions: [
        { value: '', text: 'ทั้งหมด' },
        { value: 'processing', text: 'กำลังดำเนินการ' },
        { value: 'success', text: 'สำเร็จ' },
        { value: 'cancelled', text: 'ยกเลิก' },
        { value: 'timeout', text: 'หมดเวลา' },
      ],
      showClearBlockModal: false,
      clearBlockUsername: '',
      clearBlockLoading: false,
      // Slip upload
      showSlipModal: false,
      slipTarget: null,
      slipFile: null,
      slipPreview: null,
      slipUploading: false,
      fields: [
        { key: 'index', label: 'No.' },
        { key: 'username', label: 'ยูสเซอร์เนม' },
        { key: 'amount', label: 'จำนวนเงิน', sortable: true },
        { key: 'status', label: 'สถานะ' },
        { key: 'destinationAccount', label: 'บัญชีผู้รับ' },
        { key: 'providerOrderId', label: 'Provider Ref' },
        { key: 'refNo', label: 'Ref No' },
        { key: 'externalRef', label: 'External Ref' },
        { key: 'createdAt', label: 'วันที่ทำรายการ', sortable: true },
        { key: 'actions', label: 'Actions' },
      ],
    }
  },
  mounted() { this.fetchDeposits() },
  methods: {
    async fetchDeposits() {
      this.loading = true
      try {
        const { data: res } = await this.$http.get('/autopeer-mbet/deposits', {
          params: {
            page: this.currentPage, limit: this.perPage,
            username: this.filters.username || undefined,
            status: this.filters.status || undefined,
            startDate: this.filters.startDate ? moment(this.filters.startDate).toISOString() : undefined,
            endDate: this.filters.endDate ? moment(this.filters.endDate).toISOString() : undefined,
          },
        })
        const d = res.data || res
        this.items = d.list || []
        this.totalRows = d.pagination?.total || this.items.length
      } catch (err) {
        console.error(err)
        this.$swal({ icon: 'error', title: 'เกิดข้อผิดพลาด', text: 'ไม่สามารถโหลดรายการฝากได้' })
      } finally { this.loading = false }
    },
    formatNum(v) { return Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2 }) },
    formatDate(v) { return v ? moment(v).tz('Asia/Bangkok').format('DD/MM/YYYY HH:mm:ss') : '-' },
    getDestinationAccount(item) {
      if (!item || !item.raw || !item.raw.result || !item.raw.result.list || !Array.isArray(item.raw.result.list) || item.raw.result.list.length === 0) {
        return null
      }
      const account = item.raw.result.list[0]
      return {
        bankName: account.bankName || '',
        accountNumber: account.accountNumber || '',
        firstName: account.firstName || '',
        lastName: account.lastName || '',
      }
    },
    statusClass(s) {
      if (s === 'success') return 'badge-success'
      if (s === 'cancelled' || s === 'timeout') return 'badge-danger'
      return 'badge-info'
    },

    // --- Slip Upload ---
    openSlipModal(item) {
      this.slipTarget = item
      this.showSlipModal = true
    },
    previewSlip(file) {
      if (file) {
        const reader = new FileReader()
        reader.onload = (e) => { this.slipPreview = e.target.result }
        reader.readAsDataURL(file)
      } else {
        this.slipPreview = null
      }
    },
    resetSlipModal() {
      this.slipTarget = null
      this.slipFile = null
      this.slipPreview = null
      this.slipUploading = false
    },
    async submitSlip(bvModalEvt) {
      bvModalEvt.preventDefault()
      if (!this.slipFile || !this.slipTarget) return

      this.slipUploading = true
      try {
        const formData = new FormData()
        formData.append('username', this.slipTarget.username)
        formData.append('file', this.slipFile)

        const { data: res } = await this.$http.post('/autopeer-mbet/upload-slip', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })

        const d = res.data || {}
        const code = d.code || d.data?.code
        const msg = d.message || d.data?.message || res.message || 'ไม่สามารถอัปโหลดสลิปได้'

        if (res.success && (!code || code === '0' || code === 0)) {
          this.$swal({ icon: 'success', title: 'สำเร็จ', text: `อัปโหลดสลิปสำหรับ ${this.slipTarget.username} เรียบร้อยแล้ว` })
          this.showSlipModal = false
          this.resetSlipModal()
          this.fetchDeposits()
        } else {
          this.$swal({ icon: 'warning', title: 'แจ้งเตือน', text: msg })
        }
      } catch (err) {
        const msg = err.response?.data?.message || err.response?.data?.data?.message || err.message
        this.$swal({ icon: 'error', title: 'เกิดข้อผิดพลาด', text: msg })
      } finally {
        this.slipUploading = false
      }
    },

    // --- Clear Block ---
    async clearBlock(bvModalEvt) {
      if (bvModalEvt) bvModalEvt.preventDefault()
      if (!this.clearBlockUsername) return
      this.clearBlockLoading = true
      try {
        const { data: res } = await this.$http.post('/autopeer-mbet/clear-block', {
          username: this.clearBlockUsername,
        })
        const d = res.data || {}
        const code = d.code || d.data?.code
        const msg = d.message || d.data?.message || res.message

        if (res.success && (!code || code === '0' || code === 0)) {
          this.$swal({ icon: 'success', title: 'สำเร็จ', text: `ปลด Block สำเร็จสำหรับ ${this.clearBlockUsername}` })
          this.clearBlockUsername = ''
          this.showClearBlockModal = false
        } else {
          this.$swal({ icon: 'warning', title: 'แจ้งเตือน', text: msg || 'ไม่สามารถปลด Block ได้' })
        }
      } catch (err) {
        const msg = err.response?.data?.message || err.response?.data?.data?.message || err.message
        this.$swal({ icon: 'error', title: 'เกิดข้อผิดพลาด', text: msg })
      } finally {
        this.clearBlockLoading = false
      }
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-flatpicker.scss";
</style>
